import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import type { StorySummary } from '../../types/board.js';
import * as boardApi from '../../services/board-api.js';

const MODEL_OPTIONS = [
  { value: 'claude-sonnet-4-6', label: 'Sonnet' },
  { value: 'claude-opus-4-6', label: 'Opus' },
  { value: 'claude-haiku-4-5-20251001', label: 'Haiku' },
] as const;

@customElement('cp-story-viewer')
export class CpStoryViewer extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) projectId = 0;
  @property({ type: String }) specId = '';
  @property({ type: Object }) story: StorySummary | null = null;
  @property({ type: String }) specModel = '';

  @state() private _content = '';
  @state() private _loading = false;
  @state() private _error = '';

  private _boundOnKeydown = this._onKeydown.bind(this);

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this._boundOnKeydown);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._boundOnKeydown);
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('story') && this.story?.storyFile) {
      void this._loadContent();
    }
  }

  private async _loadContent(): Promise<void> {
    if (!this.story?.storyFile || !this.projectId || !this.specId) return;
    this._loading = true;
    this._error = '';
    try {
      this._content = await boardApi.getStoryContent(this.projectId, this.specId, this.story.storyFile);
    } catch {
      this._error = 'Story-Inhalt konnte nicht geladen werden';
      this._content = '';
    }
    this._loading = false;
  }

  private _onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      this._close();
    }
  }

  private _onOverlayClick(e: MouseEvent): void {
    if ((e.target as HTMLElement).classList.contains('cp-story-viewer__overlay')) {
      this._close();
    }
  }

  private _close(): void {
    this.dispatchEvent(new CustomEvent('story-viewer-close', {
      bubbles: true,
      composed: true,
    }));
  }

  private _onModelChange(e: Event): void {
    e.stopPropagation();
    const model = (e.target as HTMLSelectElement).value;
    this.dispatchEvent(new CustomEvent('story-model-change', {
      bubbles: true,
      composed: true,
      detail: { storyId: this.story?.id, model },
    }));
  }

  private _renderMarkdown(text: string): ReturnType<typeof html> {
    const rawHtml = marked.parse(text, { async: false }) as string;
    const sanitized = DOMPurify.sanitize(rawHtml);
    return html`${unsafeHTML(sanitized)}`;
  }

  render() {
    if (!this.story) return nothing;

    const story = this.story;
    const currentModel = story.model ?? (this.specModel || 'claude-sonnet-4-6');

    return html`
      <div class="cp-story-viewer__overlay" @click=${this._onOverlayClick}>
        <div class="cp-story-viewer__panel">
          <div class="cp-story-viewer__header">
            <h2 class="cp-story-viewer__title">${story.title}</h2>
            <button class="cp-story-viewer__close" @click=${this._close} aria-label="Schliessen">&times;</button>
          </div>

          <div class="cp-story-viewer__meta">
            <span class="cp-story-viewer__status-dot cp-story-viewer__status-dot--${story.status}"></span>
            ${story.type ? html`<span class="cp-story-viewer__badge cp-story-viewer__badge--type">${story.type}</span>` : nothing}
            ${story.priority ? html`<span class="cp-story-viewer__badge cp-story-viewer__badge--priority cp-story-viewer__badge--${story.priority}">${story.priority}</span>` : nothing}
            ${story.effort ? html`<span class="cp-story-viewer__effort">Effort: ${story.effort}</span>` : nothing}
            <select
              class="cp-story-viewer__model-select"
              .value=${currentModel}
              @change=${this._onModelChange}
            >
              ${MODEL_OPTIONS.map(opt => html`
                <option value=${opt.value} ?selected=${currentModel === opt.value}>${opt.label}</option>
              `)}
            </select>
          </div>

          <div class="cp-story-viewer__content">
            ${this._loading
              ? html`<div class="cp-story-viewer__loading">Lade Story-Inhalt...</div>`
              : this._error
                ? html`<div class="cp-story-viewer__error">${this._error}</div>`
                : this._content
                  ? html`<div class="cp-story-viewer__markdown">${this._renderMarkdown(this._content)}</div>`
                  : html`<div class="cp-story-viewer__loading">Keine Story-Datei vorhanden</div>`
            }
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-story-viewer': CpStoryViewer;
  }
}
